import React, { useCallback, useContext } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { ActionBar, Sticky } from "@plex/react-components";

import { INotification, notification } from "infrastructure/notification";
import { ActionBarLink, DialogNavigationContext } from "ui/app/navigation";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { translationMessagesActions } from "../TranslationMessages.actions";
import { ITranslationMessagesManagerState, ITranslationMessageRow } from "../TranslationMessages.state";
import { TranslationUpload, TranslationUploadResult } from "../upload";

export interface IMessagesActionBarProps {
  downloadTranslationUploadTemplate?: () => void;
  onTranslationUploadCompleted?: (notification: INotification) => void;
  exportTranslationsToXml?: () => void;
  obsoleteTranslations?: () => void;
  isExportDisabled?: boolean;
  selectedRows?: ITranslationMessageRow[];
}

export const TranslationMessagesManagerActionBar: React.FC<IMessagesActionBarProps> = ({
  downloadTranslationUploadTemplate,
  onTranslationUploadCompleted,
  exportTranslationsToXml,
  obsoleteTranslations,
  isExportDisabled,
  selectedRows
}) => {
  const localizedValues = useLocalizedValues(() => ({
    translationUploadAction: displayMessages.action("translationsUpload"),
    selectTranslationsUploadFileAction: displayMessages.action("selectTranslationsUploadFile"),
    exportTranslationAction: displayMessages.action("exportTranslations"),
    downloadTranslationFileTemplateAction: displayMessages.action("downloadTranslationFileTemplate"),
    translationsObsoleteAction: displayMessages.action("translationsObsolete")
  }));

  const dialogNavigationContext = useContext(DialogNavigationContext);
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const exportExecute = useCallback(() => {
    exportTranslationsToXml?.();
  }, [exportTranslationsToXml]);

  const downloadXmlTemplateExecute = useCallback(() => {
    downloadTranslationUploadTemplate?.();
  }, [downloadTranslationUploadTemplate]);

  const obsoleteExecute = useCallback(() => {
    obsoleteTranslations?.();
  }, [obsoleteTranslations]);

  const inUseStatus = displayMessages.option("translationStatusInUse").value;
  const isObsoleteDisabled = !selectedRows || selectedRows.length === 0 || selectedRows.some(s => s.data.translationStatus !== inUseStatus || s.isTranslationChanged);

  return (
    <Sticky>
      <ActionBar>
        {(roles.LocalizationManager || roles.PlexLocalizationManager) && (
          <ActionBar.Button
            text={localizedValues.translationsObsoleteAction}
            title={localizedValues.translationsObsoleteAction}
            onClick={obsoleteExecute}
            disabled={isObsoleteDisabled}
          />
        )}
        {(roles.LocalizationManager || roles.PlexLocalizationManager) && (
          <ActionBar.Dropdown>
            <ActionBar.DropdownButton text={localizedValues.translationUploadAction} title={localizedValues.translationUploadAction} />
            <ActionBar.DropdownItems>
              <ActionBarLink
                text={localizedValues.selectTranslationsUploadFileAction}
                title={localizedValues.selectTranslationsUploadFileAction}
                dialog={{
                  content: (
                    <TranslationUpload
                      onUploadCompleted={error => {
                        if (onTranslationUploadCompleted) {
                          if (error) {
                            dialogNavigationContext.showDialog({
                              content: <TranslationUploadResult />
                            });
                          } else {
                            onTranslationUploadCompleted(notification.success().useMessage({ messageId: displayMessages.info("translationsUploadCompleted") }));
                          }
                        }
                      }}
                    />
                  )
                }}
              />
              <ActionBar.Divider />
              <ActionBar.Button
                text={localizedValues.downloadTranslationFileTemplateAction}
                title={localizedValues.downloadTranslationFileTemplateAction}
                onClick={downloadXmlTemplateExecute}
              />
            </ActionBar.DropdownItems>
          </ActionBar.Dropdown>
        )}

        <ActionBar.Button text={localizedValues.exportTranslationAction} title={localizedValues.exportTranslationAction} onClick={exportExecute} disabled={isExportDisabled} />
      </ActionBar>
    </Sticky>
  );
};

export default connect(
  (state: { translationMessagesManager: ITranslationMessagesManagerState }) => {
    return {
      isExportDisabled: state.translationMessagesManager.isExportDisabled,
      selectedRows: state.translationMessagesManager.grid.selected
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        exportTranslationsToXml: translationMessagesActions.exportTranslationsToXml,
        onTranslationUploadCompleted: translationMessagesActions.onTranslationUploadCompleted,
        downloadTranslationUploadTemplate: translationMessagesActions.downloadTranslationUploadTemplate,
        obsoleteTranslations: translationMessagesActions.obsoleteTranslations
      },
      dispatch
    );
  }
)(TranslationMessagesManagerActionBar);
